import { CheckOutlined } from "@mui/icons-material";
import { DialogContent, Typography } from "@mui/material";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
	MoleCancelButton,
	MoleDialog,
	MoleDialogActions,
	MoleDialogHeader,
	MolePrimaryButton,
	useMoleThemeColor,
} from "./MoleDialogShell";
import { Z_INDEX } from "../styles/zIndex";

interface Props {
	isOpen: boolean;
	setIsOpen: (isOpen: boolean) => void;
	title?: ReactNode;
	message?: ReactNode;
	confirmBtnColor?: "primary" | "secondary" | "success" | "error" | "info" | "inherit" | "warning";
	confirmBtnVariant?: "text" | "outlined" | "contained";
	confirmBtnText?: ReactNode;
	confirmBtnIcon?: ReactNode;
	onConfirm?: () => void;
	onCancel?: () => void;
	sx?: object;
}

const ConfirmDialog = ({
	isOpen,
	setIsOpen,
	title,
	message,
	confirmBtnColor = "success",
	confirmBtnVariant = "contained",
	confirmBtnText,
	confirmBtnIcon = <CheckOutlined />,
	onConfirm,
	onCancel,
	sx,
}: Props) => {
	const { t } = useTranslation();
	const themeColor = useMoleThemeColor();
	const resolvedTitle = title ?? t("common:dialog.confirmation");
	const resolvedMessage = message ?? t("common:dialog.proceedQuestion");
	const resolvedConfirmBtnText = confirmBtnText ?? t("common:ui.buttons.confirm");
	const toggle = () => setIsOpen(!isOpen);

	const handleConfirm = () => {
		onConfirm?.();
		toggle();
	};

	const handleCancel = () => {
		onCancel?.();
		toggle();
	};

	return (
		<MoleDialog
			open={isOpen}
			onClose={handleCancel}
			maxWidth="xs"
			fullWidth
			themeColor={themeColor}
			sx={{ zIndex: Z_INDEX.confirm, ...sx }}
		>
			<MoleDialogHeader
				title={resolvedTitle}
				icon={<CheckOutlined />}
				onClose={handleCancel}
				closeLabel={t("common:ui.buttons.cancel")}
				themeColor={themeColor}
			/>
			<DialogContent sx={{ pt: 1, pb: 2 }}>
				{typeof resolvedMessage === "string" ? (
					<Typography variant="body2" color="text.secondary">
						{resolvedMessage}
					</Typography>
				) : (
					resolvedMessage
				)}
			</DialogContent>
			<MoleDialogActions>
				<MoleCancelButton onClick={handleCancel}>{t("common:ui.buttons.cancel")}</MoleCancelButton>
				<MolePrimaryButton
					color={confirmBtnColor}
					variant={confirmBtnVariant}
					onClick={handleConfirm}
					startIcon={confirmBtnIcon}
				>
					{resolvedConfirmBtnText}
				</MolePrimaryButton>
			</MoleDialogActions>
		</MoleDialog>
	);
};

export default ConfirmDialog;